import React, { useState } from 'react';
import { Table, Button, Select, Space, Card, Typography, Tooltip, Modal, App } from 'antd';
import { Link } from 'react-router-dom';
import {
    EditOutlined,
    ExclamationCircleOutlined,
    SettingOutlined
} from '@ant-design/icons';
import { showSuccessNotification, showErrorNotification } from '@/shared/utils/notify';
import {ActionFlowConfigurationDTO, ActionFlowSubscriptionDTO} from "@/shared/api/model";
import {
    useActionFlowConfigurations, useActionFlowSubscriptions,
    useManageSubscription
} from "@/modules/action-flow/config/hooks/use-action-flow";

const { Title } = Typography;
const { Option } = Select;

interface SubscriptionListProps {
    // These props are mentioned in the paste-2.txt but not used
    archived?: boolean;
    onSubscriptionSelect?: (subscription: ActionFlowSubscriptionDTO | null) => void;
    selectedSubscription?: ActionFlowSubscriptionDTO | null;
}


export const SubscriptionList: React.FC<SubscriptionListProps> = ({
                                                                      archived = false,
                                                                      onSubscriptionSelect,
                                                                      selectedSubscription
                                                                  }) => {
    const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
    const { subscriptions, isLoading: isLoadingSubscriptions, refetch: refetchSubscriptions } = useActionFlowSubscriptions();
    const { configurations, isLoading: isLoadingFlows } = useActionFlowConfigurations();
    const { assignFlow } = useManageSubscription();

    const { modal } = App.useApp();

    const handleAssignFlow = async (subscription: ActionFlowSubscriptionDTO, flowId: string) => {
        if (!subscription.domain || !subscription.subscriptionId) {
            showErrorNotification('Invalid subscription details');
            return;
        }

        const selectedFlow = configurations?.find(flow => flow.id === flowId);

        modal.confirm({
            title: 'Confirm Flow Assignment',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to assign the flow "${selectedFlow?.name}" to the subscription "${subscription.name}"?`,
            onOk: async () => {
                try {
                    await assignFlow({
                        // @ts-ignore
                        domain: subscription.domain,
                        // @ts-ignore
                        subscriptionId: subscription.subscriptionId,
                        flowId
                    });
                    // Success message is already handled in the assignFlow hook
                    // Refresh the subscriptions list to show the updated mapping
                    await refetchSubscriptions();
                } catch (error) {
                    // Error message is already handled in the assignFlow hook
                    console.error('Flow assignment error:', error);
                }
            }
        });
    };

    const domains = [...new Set(subscriptions?.map(s => s.domain))];

    const columns = [
        {
            title: 'Subscription ID',
            dataIndex: 'subscriptionId',
            key: 'subscriptionId',
            width: '15%'
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%'
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: '25%'
        },
        {
            title: 'Assigned Flow',
            dataIndex: 'flowName',
            key: 'assignedFlow',
            width: '25%',
            render: (text: string, record: ActionFlowSubscriptionDTO) => (
                <Select
                    style={{ width: '100%' }}
                    placeholder="Select a flow"
                    onChange={(value) => handleAssignFlow(record, value)}
                    value={record.flowId}
                    loading={isLoadingFlows}
                >
                    {configurations?.map((flow: ActionFlowConfigurationDTO) => (
                        <Option key={flow.id} value={flow.id}>
                            {flow.name} {flow.version ? `(v${flow.version})` : ''}
                        </Option>
                    ))}
                </Select>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '15%',
            render: (text: string, record: ActionFlowSubscriptionDTO) => (
                <Space>
                    <Tooltip title="Edit Configuration">
                        <Link to={`/admin/action-flow/subscriptions/${record.domain}/${record.subscriptionId}/config`}>
                            <Button icon={<EditOutlined />} />
                        </Link>
                    </Tooltip>
                    <Tooltip title="Dynamic Form Settings">
                        <Button
                            icon={<SettingOutlined />}
                            onClick={() => {
                                // Handle dynamic form settings
                            }}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <Title level={2}>Action Flow Subscriptions</Title>
                <Select
                    style={{ width: 200 }}
                    placeholder="Select Domain"
                    onChange={(value) => setSelectedDomain(value)}
                    allowClear
                >
                    {domains.map((domain) => (
                        <Option key={domain} value={domain}>{domain}</Option>
                    ))}
                </Select>
            </div>

            <Table
                columns={columns}
                dataSource={subscriptions?.filter(s => !selectedDomain || s.domain === selectedDomain)}
                rowKey={(record) => `${record.domain}-${record.subscriptionId}`}
                loading={isLoadingSubscriptions}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true
                }}
                className="subscription-table"
                rowSelection={{
                    type: 'radio',
                    selectedRowKeys: selectedSubscription ? [`${selectedSubscription.domain}-${selectedSubscription.subscriptionId}`] : [],
                    onChange: (_, selectedRows) => {
                        onSubscriptionSelect?.(selectedRows[0] as ActionFlowSubscriptionDTO);
                    }
                }}
                // Add this to highlight the selected row
                onRow={(record) => ({
                    onClick: () => onSubscriptionSelect?.(record),
                })}
            />
        </Card>
    );
};

export default SubscriptionList;